import React, { use, useCallback } from "react";
import { ScrollView } from "react-native";
import { useI18n } from "../../hooks/i18n-hooks";
import { useMutation } from "@apollo/client/react";
import { UPDATE_WORKER_PROFILE } from "../../graphql/core";
import { AuthManagerContext } from "../../contexts/AuthManager";
import VStack from "../../ui_atoms/VStack";
import ScreenCard from "../ScreenCard";
import { useApolloErrorSnackbar } from "../../hooks/snackbar";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useCloseEditor } from "../profile-editor/use-close-editor";
import { DocumentType } from "../../__generated__/graphql";
import DocumentButton from "../../components/document/DocumentButton";
import { DisplayableDocument } from "../../contexts/DocumentManager";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { XOR } from "ts-essentials";
import { BodyMediumText, BodySmallText } from "../../components/FormattedText";
import OptionsField from "../../components/OptionsField";
import { useTheme } from "../../hooks/use-theme";
import ListItem from "../../ui_atoms/ListItem";
import { useIntercom } from "../../hooks/intercom";
import InfoButton from "../../components/InfoButton";

type FormData = XOR<
    { hasFiscalAttestation?: false },
    {
        hasFiscalAttestation: true;
        fiscalAttestation: DisplayableDocument;
    }
>;

type FiscalAttestationEditorScreenProps = StackScreenProps<
    RootStackParamList,
    "FiscalAttestationEdit"
>;

const FiscalAttestationEditorScreen = ({
    navigation,
    route,
}: FiscalAttestationEditorScreenProps) => {
    const { profileId } = route.params || {};
    const { user, refreshUser } = use(AuthManagerContext);
    const { presentArticleId } = useIntercom();
    const [updateWorkerProfile, { error: saveError, loading: saving, reset }] = useMutation(
        UPDATE_WORKER_PROFILE,
        profileId ? { context: { liftProfile: profileId } } : undefined,
    );

    useApolloErrorSnackbar(saveError, reset);

    const i18n = useI18n();
    const { colors } = useTheme();

    const {
        handleSubmit,
        control,
        watch,
        formState: { isValid, isDirty },
    } = useForm<FormData>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            hasFiscalAttestation: user?.workerProfile?.hasFiscalAttestation || undefined,
            fiscalAttestation:
                user?.workerProfile?.documents?.find(
                    (d) => d.type === DocumentType.FiscalAttestation,
                ) || undefined,
        },
    });

    const hasFiscalAttestation = watch("hasFiscalAttestation");

    const onSubmit = useCallback<SubmitHandler<FormData>>(
        async (data) => {
            try {
                await updateWorkerProfile({
                    variables: {
                        changes: {
                            hasFiscalAttestation: data.hasFiscalAttestation,
                            ...(data.hasFiscalAttestation && data.fiscalAttestation
                                ? {
                                      documents: [
                                          ...(user?.workerProfile?.documents || []),
                                          data.fiscalAttestation,
                                      ],
                                  }
                                : {}),
                        },
                    },
                });
                navigation.goBack();
                void refreshUser?.();
            } finally {
                // this is handled by updateError in useMutation
            }
        },
        [navigation, refreshUser, user?.workerProfile?.documents, updateWorkerProfile],
    );

    useCloseEditor(isDirty && !saving);

    return (
        <ScreenCard
            buttons={[
                {
                    title: i18n.t("save"),
                    onPress: handleSubmit(onSubmit),
                    validating: saving,
                    isValid: isValid && isDirty,
                    mode: "secondary",
                },
            ]}
            withAssistanceButton
        >
            <ScrollView>
                <VStack v={1} gap={0.5} alignment="stretch" h="2/3">
                    <VStack gap={0.25}>
                        <BodyMediumText>
                            <BodyMediumText>
                                {i18n.t(
                                    "screens.profile_editor.fiscal_attestation.has_fiscal_attestation",
                                )}
                            </BodyMediumText>
                            <InfoButton
                                onPress={() => {
                                    void presentArticleId("11178258");
                                }}
                            />
                        </BodyMediumText>
                        <Controller
                            control={control}
                            name="hasFiscalAttestation"
                            rules={{
                                validate: (v) => v !== undefined,
                            }}
                            render={({
                                field: { onChange, onBlur, value },
                                fieldState: { error },
                            }) => (
                                <OptionsField
                                    value={value}
                                    data={[
                                        { label: i18n.t("yes"), value: true },
                                        { label: i18n.t("no"), value: false },
                                    ]}
                                    onSelect={onChange}
                                    onBlur={onBlur}
                                    error={error?.message}
                                    shrinked
                                />
                            )}
                        />
                    </VStack>
                </VStack>

                {hasFiscalAttestation && (
                    <Controller
                        control={control}
                        name="fiscalAttestation"
                        rules={{
                            validate: (v) => v !== undefined,
                        }}
                        render={({ field: { onChange, value } }) =>
                            !value ? (
                                <DocumentButton
                                    asListAction="primary"
                                    label={i18n.t(
                                        "screens.profile_editor.fiscal_attestation.import_fiscal_attestation",
                                    )}
                                    right="add"
                                    onSelect={onChange}
                                    documentType={DocumentType.FiscalAttestation}
                                    fixedName={i18n.t(
                                        "screens.profile_editor.fiscal_attestation.title",
                                    )}
                                    saveButtonTitle={i18n.t("import")}
                                />
                            ) : (
                                <ListItem
                                    description={value.title}
                                    rightIcon="trash"
                                    rightActionMode="action_simple"
                                    rightIconColor={colors.primary}
                                    onPress={() => {
                                        onChange();
                                    }}
                                />
                            )
                        }
                    />
                )}

                {hasFiscalAttestation !== undefined && (
                    <VStack v={hasFiscalAttestation ? 1 : 0} gap={0.5} alignment="stretch" h="2/3">
                        <BodySmallText color={colors.fieldLabel}>
                            {hasFiscalAttestation
                                ? i18n.t(
                                      "screens.profile_editor.fiscal_attestation.has_fiscal_attestation_description",
                                  )
                                : i18n.t(
                                      "screens.profile_editor.fiscal_attestation.has_no_fiscal_attestation_description",
                                  )}
                        </BodySmallText>
                    </VStack>
                )}
            </ScrollView>
        </ScreenCard>
    );
};

export default FiscalAttestationEditorScreen;
